import React from 'react';
import { useViewsContext } from '../../../context/view/ViewsContext';
import { useGetView } from '../../../context/view/useGetView';
import { useGlobalizationContext } from '../../../context/globalization/GlobalizationContext';
import { GearFieldRequest } from '../../../Model/GearFieldRequest';
import { UpdateViewRequest } from '../../../Model/requests/UpdateViewRequest';
import { useUpdateView } from '../../../context/view/useUpdateView';
import { Button } from '@progress/kendo-react-buttons';
import { saveIcon } from '@progress/kendo-svg-icons';
import { SvgIcon } from '@progress/kendo-react-common';
import { useTranslation } from '../../../context/globalization/useTranslation';

interface SaveViewChangesButtonProps {
  columnChanges: {
    widths: Record<string, number>;
    order: string[];
  };
  columns?: any[];
  onSaveComplete?: () => void;
}

export const SaveViewChangesButton: React.FC<SaveViewChangesButtonProps> = ({
  columnChanges,
  columns,
  onSaveComplete
}) => {
  const updateViewMutation = useUpdateView();
  const { selectedView, selectedViewData } = useViewsContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { data: currentView } = useGetView(selectedView, selectedLocale);

  const hasChanges = React.useMemo(() => {
    if (!columns || !currentView?.fields) {
      return false;
    }

    const hasWidthChanges = Object.entries(columnChanges.widths).some(([fieldName, newWidth]) => {
      const originalField = currentView.fields.find(f => f.name === fieldName);
      return originalField && originalField.width !== newWidth;
    });

    const originalOrder = currentView.fields.map(field => field.name);
    const hasOrderChanges = !columnChanges.order.every((item, index) => item === originalOrder[index]);

    return hasWidthChanges || hasOrderChanges;
  }, [columnChanges, columns, currentView?.fields]);

  const saveColumnChanges = async () => {
    if (!currentView || !columns || !hasChanges || !selectedViewData) {
      return;
    }

    const orderedFields = columnChanges.order
      .map(fieldName => columns.find(col => col.name === fieldName))
      .filter((field): field is NonNullable<typeof field> => Boolean(field))
      .map(field => {
        const newWidth = columnChanges.widths[field.name];

        return {
          id: field.id,
          width: newWidth !== undefined ? newWidth : field.width,
          freezed: field.freezed || false,
          isDocumentLink: field.isDocumentLink,
          facetsCount: field.facetsCount
        } as GearFieldRequest;
      });

    const updateRequest: UpdateViewRequest = {
      viewId: currentView.id,
      name: selectedViewData.name,
      isDefault: selectedViewData.isDefault,
      fields: orderedFields
    };

    try {
      await updateViewMutation.mutateAsync(updateRequest);
      onSaveComplete?.();
    } catch {
      // empty
    }
  };

  if (!hasChanges) {
    return null;
  }

  return (
    <Button
      fillMode="solid"
      themeColor={'primary'}
      size={'small'}
      onClick={updateViewMutation.isPending ? undefined : saveColumnChanges}
      title={translate('custom.toolbar.saveViewChanges')}
      className="pagerButton"
      disabled={updateViewMutation.isPending}
      endIcon={<SvgIcon icon={saveIcon} />}
    >
      {translate('custom.toolbar.saveViewChanges')}
    </Button>
  );
};
